import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { assetQuery } from "../api/assetlib";
import type { AssetDetail, Dressing, NestedAsset } from "../api/types";

type Defaults = AssetDetail["defaults"];

/** Every surface and artwork a dressed object needs, keyed `material/<id>`
 *  and `skin/<id>`: what the detail already carries (its own defaults and,
 *  for a look, its object's) and, for each id the user picked beyond those,
 *  that id's own detail, asked for under the key every other reader uses so
 *  nothing is fetched twice. An id still loading is absent. The viewer and
 *  the surfaces panel both read this, so they always agree. */
export function useDressingDetails(
  detail: { defaults: Defaults; shape?: { defaults: Defaults } },
  dressing: Dressing,
): Record<string, NestedAsset> {
  const own = detail.defaults;
  const shapes = detail.shape?.defaults;
  const known = useMemo(() => {
    const out: Record<string, NestedAsset> = {};
    for (const defaults of [shapes, own]) {
      if (!defaults) continue;
      for (const [id, one] of Object.entries(defaults.materials)) {
        out["material/" + id] = one;
      }
      for (const [id, one] of Object.entries(defaults.skins)) {
        out["skin/" + id] = one;
      }
    }
    return out;
  }, [own, shapes]);

  // What the user picked that the detail does not carry, as one string, so
  // the list below changes only when its content does.
  const wanted = [
    ...Object.values(dressing.materials).map((id) => "material/" + id),
    ...Object.values(dressing.skins)
      .filter((value): value is string => typeof value === "string")
      .map((id) => "skin/" + id),
  ].filter((key) => !(key in known));
  const missing = [...new Set(wanted)].sort().join("\n");
  const keys = useMemo(() => (missing ? missing.split("\n") : []), [missing]);

  const fetched = useQueries({
    queries: keys.map((key) => {
      const at = key.indexOf("/");
      return assetQuery(
        key.slice(0, at) as "material" | "skin",
        key.slice(at + 1),
      );
    }),
  });
  // One string again: `useQueries` gives a new array on every render.
  const arrived = fetched
    .map((query, i) => (query.data ? `${keys[i]}@${query.dataUpdatedAt}` : ""))
    .join("\n");

  return useMemo(() => {
    const out = { ...known };
    fetched.forEach((query, i) => {
      if (query.data) out[keys[i]] = query.data;
    });
    return out;
    // `fetched` is read through `arrived`, which changes exactly when a
    // detail arrives or is refreshed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [known, keys, arrived]);
}
